import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import HubCountryLinks from "@/components/HubCountryLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { SWORN_LANGUAGES } from "@/data/sworn-languages";

const TITLE = "Sworn Translator ประเทศไทย — นักแปลสาบานตน 20+ ภาษา | NYC";
const DESC = "บริการนักแปลสาบานตน (Sworn Translator) ขึ้นทะเบียนศาล/MoJ/สถานทูตต่างประเทศ 20+ ภาษา — คุยกับผู้เชี่ยวชาญตัวจริง ตอบกลับภายในไม่กี่นาที";

const steps = [
  { name: "ตรวจปลายทาง", text: "แจ้งประเทศและหน่วยงานที่จะยื่น เพื่อเลือกประเภท sworn ที่ถูกต้อง (§189 GVG เยอรมัน / Cour d'appel ฝรั่งเศส / MAEC สเปน / MoJ ไทย ฯลฯ)" },
  { name: "ส่งเอกสาร 300dpi", text: "รับต้นฉบับผ่าน LINE/Email — ประเมินราคา+เวลาภายใน 30 นาที" },
  { name: "แปลโดย Sworn Translator", text: "นักแปลที่ได้รับการสาบานตนตามกฎหมายประเทศปลายทาง มีเลขทะเบียนตรวจสอบได้" },
  { name: "QA 4-eyes + ตราประทับ", text: "ตรวจซ้ำโดยผู้เชี่ยวชาญคนที่สอง แล้วประทับตราสาบานตน + ใบ Certificate of Accuracy" },
  { name: "MFA + Apostille (ถ้าจำเป็น)", text: "ต่อยอดกงสุลไทย + Apostille (Hague 15 ธ.ค. 2025) หรือ Embassy Legalization ครบในแพ็กเกจเดียว" },
  { name: "จัดส่งทั่วโลก", text: "EMS ฟรีในไทย, DHL/FedEx ต่างประเทศ 2–5 วันทำการ" },
];

const faqs = [
  { q: "Sworn Translator ต่างจาก Certified Translator อย่างไร?", a: "Sworn Translator คือ นักแปลที่สาบานตนต่อศาลหรือหน่วยงานรัฐของประเทศปลายทาง (เช่น beeidigter Übersetzer เยอรมัน, Traducteur assermenté ฝรั่งเศส) — คำแปลมีสถานะเป็นเอกสารทางกฎหมายทันที ไม่ต้อง Notary ซ้ำ ส่วน Certified Translator แค่แสดงตัวตนนักแปลกับใบรับรอง" },
  { q: "ประเทศไหนบังคับใช้ Sworn Translator?", a: "เยอรมนี ฝรั่งเศส สเปน อิตาลี เนเธอร์แลนด์ โปแลนด์ โปรตุเกส บราซิล อาร์เจนตินา รวมถึงบางกรณีในอินโดนีเซีย (Penerjemah Tersumpah). อังกฤษ สหรัฐฯ ออสเตรเลีย แคนาดา ญี่ปุ่น เกาหลี ไม่ใช้ระบบ sworn — ใช้ Certified/NAATI/MoJ แทน" },
  { q: "ราคาเริ่มต้นเท่าไร?", a: "อังกฤษ ฿1,500/หน้า — จีน/ญี่ปุ่น/เกาหลี ฿1,800–2,000 — เยอรมัน/ฝรั่งเศส/สเปน/อิตาลี ฿2,400 — อาหรับ/รัสเซีย/ดัตช์/โปแลนด์ ฿2,600–2,800 — รวมตราสาบานตนและใบ Certificate of Accuracy" },
  { q: "ใช้เวลานานแค่ไหน?", a: "ภาษาหลัก (อังกฤษ จีน ญี่ปุ่น เกาหลี) 1–3 วันทำการ สำหรับเอกสารมาตรฐานไม่เกิน 10 หน้า · ภาษายุโรป (สเปน ฝรั่งเศส เยอรมัน อิตาลี โปรตุเกส) 3–5 วันทำการ · ภาษาหายาก (อาหรับ รัสเซีย ตุรกี ฮินดี เวียดนาม) 5–7 วันทำการ · Express 24 ชั่วโมง +50% ทุกภาษา · งาน rush 4 ชั่วโมง +100% เฉพาะภาษาหลัก · ตอบใบเสนอราคาภายใน 15 นาทีเวลาทำการ 09:00–18:00" },
  { q: "ต้องรับรอง MFA + Apostille เพิ่มไหม?", a: "ขึ้นกับปลายทาง: เอกสารส่งเยอรมัน/ฝรั่งเศส/สเปนที่แปลโดย sworn ท้องถิ่นแล้ว มัก 'ไม่ต้อง' MFA เพิ่ม — แต่ถ้าแปลในไทยแล้วส่งไป EU ต้อง MFA ไทย + Apostille ก่อน" },
  { q: "รับแปลเอกสารประเภทใด?", a: "คำพิพากษา สัญญา หนังสือมอบอำนาจ พินัยกรรม ทะเบียนบริษัท งบการเงินตรวจสอบ ทรานสคริปต์ ปริญญาบัตร ทะเบียนสมรส/หย่า สูติบัตร มรณบัตร รายงานทางการแพทย์ patent specification" },
  { q: "ตรวจสอบทะเบียนนักแปลได้ที่ไหน?", a: "เยอรมนี: justiz-dolmetscher.de (Landesjustizverwaltungen) — ฝรั่งเศส: annuaire.cour-de-cassation.fr (Cour d'appel) — สเปน: exteriores.gob.es MAEC — อิตาลี: Tribunale CTU list — ไทย: coj.go.th ศาลยุติธรรม NYC ระบุเลขทะเบียนบน Certificate of Accuracy ทุกครั้ง เพื่อให้ปลายทางตรวจสอบได้ทันที" },
  { q: "รับงานทั่วโลกไหม?", a: "รับ — มีเครือข่าย sworn translator ประจำในเบอร์ลิน มิวนิก แฟรงก์เฟิร์ต ปารีส ลียง มาร์เซย มาดริด บาร์เซโลนา โรม มิลาน อัมสเตอร์ดัม วอร์ซอ ลิสบอน จัดส่งเอกสารตัวจริง DHL Express 2–5 วันทำการครอบคลุม 220+ ประเทศ พร้อม tracking และเวอร์ชัน PDF ลงลายเซ็นดิจิทัลส่งอีเมลในวันเดียวกัน" },
];

export const Route = createFileRoute("/sworn-translator/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/sworn-translator", "th")}, { name: "description", content: ctaDesc(DESC, "/sworn-translator", "th")},
      { name: "keywords", content: "sworn translator, นักแปลสาบานตน, beeidigter Übersetzer, traducteur assermenté, traductor jurado, sworn translation Thailand" },
      { property: "og:title", content: ctaTitle(TITLE, "/sworn-translator", "th")}, { property: "og:description", content: ctaDesc(DESC, "/sworn-translator", "th")},
      { property: "og:url", content: "https://nyclanguageinstitute.com/sworn-translator" },
    ],
    links: canonicalAndHreflang("/sworn-translator", "/en/sworn-translator"),
    scripts: buildLandingSchemas({
      path: "/sworn-translator", title: TITLE, description: DESC,
      serviceType: "Sworn Translation Service", priceFromThb: 1500, steps, faqs,
      breadcrumbs: [{ name: "Home", path: "/" }, { name: "บริการ", path: "/services" }, { name: "Sworn Translator", path: "/sworn-translator" }],
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <ServiceLanding
        badge="Court-sworn · 20+ languages"
        title="Sworn Translator ประเทศไทย — 20+ ภาษา"
        subtitle={DESC}
        quickAnswer={{
          q: "Sworn Translator คืออะไร ต่างจาก Certified อย่างไร?",
          a: "นักแปลที่สาบานตนต่อศาลหรือหน่วยงานรัฐของประเทศปลายทาง เช่น beeidigter Übersetzer (เยอรมัน), Traducteur assermenté (ฝรั่งเศส) — คำแปลใช้ทางกฎหมายได้ทันทีโดยไม่ต้อง Notary ซ้ำ NYC ให้บริการ 20+ ภาษา เริ่ม ฿1,500/หน้า พร้อมต่อยอด MFA + Apostille",
        }}
        heroBullets={[
          "20+ ภาษา (EN/CN/JP/KR/DE/FR/ES/IT/NL/PL/AR/RU/HI/VI/ID ฯลฯ)",
          "นักแปลขึ้นทะเบียนตามกฎหมายประเทศปลายทาง — ตรวจสอบเลขทะเบียนได้",
          "QA 4-eyes ตามมาตรฐาน ISO 17100:2015+A1:2024",
          "ต่อยอด MFA + Apostille + Embassy ในแพ็กเกจเดียว",
        ]}
        whoFor={{
          th: ["ผู้ยื่นเอกสารต่อศาล/หน่วยราชการต่างประเทศ (EU/LATAM)", "นักเรียนที่ต้อง credential evaluation ใน DE/FR/ES/IT", "บริษัทเซ็นสัญญาข้ามชาติ", "คู่สมรสที่ต้องจดทะเบียนใน EU"],
          foreign: ["Foreigners in Thailand needing sworn translations for home country", "Law firms handling cross-border litigation"],
        }}
        steps={steps}
        pricing={SWORN_LANGUAGES.slice(0, 10).map((l) => ({ label: `${l.th} (${l.en})`, price: `${l.priceFrom.toLocaleString()}/หน้า`, note: l.turnaround }))}
        faqs={faqs}
        relatedLinks={[
          { to: "/moj-translator", label: "MoJ Sworn Translator" },
          { to: "/translate/mfa", label: "แปล + รับรอง MFA" },
          { to: "/notary", label: "Notary Public" },
          { to: "/apostille", label: "Apostille (Hague)" },
          { to: "/embassy", label: "Embassy Legalization" },
          { to: "/services", label: "บริการทั้งหมด" },
        ]}
      />
      <section className="mx-auto max-w-5xl px-4 py-12">
        <h2 className="text-2xl font-bold">เลือกภาษา Sworn Translator</h2>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {SWORN_LANGUAGES.map((l) => (
            <Link key={l.slug} to="/sworn-translator/$language" params={{ language: l.slug }}
              className="rounded border p-3 hover:border-primary">
              <div className="font-semibold">{l.th}</div>
              <div className="text-xs text-muted-foreground">{l.en} · เริ่ม ฿{l.priceFrom.toLocaleString()}/หน้า · {l.turnaround}</div>
              <div className="text-xs mt-1 text-primary">{l.registryTh}</div>
            </Link>
          ))}
        </div>
        <div className="mt-8 rounded-lg border border-border p-4">
          <h2 className="text-xl font-semibold">คู่มือเอกสารที่ต้องแปลและรับรองคำแปล</h2>
          <p className="mt-2 text-sm leading-7 text-muted-foreground">
            รวม 6 กลุ่มเอกสาร (คดีความ/ศาล, ทะเบียนราษฎร, การศึกษา, นิติบุคคล, ตรวจคนเข้าเมือง, การแพทย์-ประกัน)
            พร้อมลำดับขั้นตอน เช็กลิสต์ และคำถามที่พบบ่อยของแต่ละกลุ่ม
          </p>
          <Link to="/sworn-translator/documents" className="mt-3 inline-block text-primary hover:underline">
            ดูคู่มือเอกสารทั้ง 6 กลุ่ม →
          </Link>
        </div>
      </section>
      <HubCountryLinks service="apostille" lang="th" />
    </>
  );
}
